import type { MatchEvent, MatchState, Replay, Side } from '../engine';
import type { Controller, MatchHost } from './matchHost';
import { MATCH_RECORD_VERSION, type MatchRecord, type SeatInfo } from './matchRecord';
import { readJson, writeJson } from './storage';
import { anonymousId } from './telemetryStore';
import { APP_COMMIT, APP_VERSION } from './version';

/**
 * Uploads matches to the playtest server (functions/api/matches.ts) so they can be analysed
 * (scripts/analyse-matches.ts). A match is sent when a round ends, when it ends, when the player
 * leaves it and when the page is hidden; every upload replaces the previous one. Uploads that fail
 * for network or server reasons wait in a small queue and are retried on the next visit.
 * The player can switch this off in Settings (shareMatches).
 */

const ENDPOINT = 'api/matches';
const QUEUE_KEY = 'match-log.queue.v1';
const SENT_KEY = 'match-log.sent.v1';
const MAX_QUEUED = 20;
const MAX_SENT_IDS = 500;

let enabled = false;
/** Counts the times uploading was turned off: a flush under way stops before its next upload. */
let generation = 0;

/** Turns uploading on or off (the shareMatches setting); turning it on retries the queue. */
export function setMatchLogging(on: boolean): void {
  const was = enabled;
  enabled = on;
  if (!on) generation++;
  if (on && !was) void flushMatchQueue();
}

export const newMatchId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `m-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e12).toString(36)}`;

function device(): MatchRecord['device'] {
  try {
    return globalThis.matchMedia?.('(hover: hover) and (pointer: fine)').matches === false
      ? 'touch'
      : 'mouse';
  } catch {
    return 'mouse';
  }
}

/** What the logger needs to know about a match; read lazily, so it always sends the latest. */
export interface MatchLogSource {
  readonly id: string;
  readonly mode: MatchRecord['mode'];
  readonly startedAt: string;
  readonly campaign?: MatchRecord['campaign'];
  readonly controllers: () => Readonly<Record<Side, Controller>>;
  readonly replay: () => Replay;
  readonly state: () => MatchState;
}

/** A source reading a match host. */
export const hostSource = (
  host: MatchHost,
  info: Pick<MatchLogSource, 'id' | 'mode' | 'startedAt' | 'campaign'>,
): MatchLogSource => ({
  ...info,
  controllers: () => host.getSnapshot().controllers,
  replay: () => host.replay,
  state: () => host.currentState,
});

const seatOf = (c: Controller): SeatInfo =>
  c.kind === 'ai' ? { kind: 'ai', difficulty: c.difficulty } : { kind: 'human' };

export function buildRecord(src: MatchLogSource, now = new Date()): MatchRecord {
  const s = src.state();
  const controllers = src.controllers();
  const replay = src.replay();
  return {
    v: MATCH_RECORD_VERSION,
    id: src.id,
    player: anonymousId(),
    app: { version: APP_VERSION, commit: APP_COMMIT },
    site: typeof location !== 'undefined' ? location.host : 'unknown',
    device: device(),
    mode: src.mode,
    startedAt: src.startedAt,
    updatedAt: now.toISOString(),
    finished: s.phase === 'MATCH_OVER',
    sides: { S: seatOf(controllers.S), N: seatOf(controllers.N) },
    replay,
    summary: {
      round: s.round,
      phase: s.phase,
      winner: s.result?.winner ?? null,
      reason: s.result?.reason ?? null,
      totals: { S: s.players.S.totalScore, N: s.players.N.totalScore },
      rounds: s.rounds,
      actions: replay.actions.length,
    },
    ...(src.campaign ? { campaign: src.campaign } : {}),
  };
}

// ---- Delivery and the retry queue -------------------------------------------------------------

const loadQueue = (): MatchRecord[] =>
  readJson(QUEUE_KEY, (v) => (Array.isArray(v) ? (v as MatchRecord[]) : null)) ?? [];

/** Records waiting to be sent (tests and diagnostics). */
export const queuedMatches = (): readonly MatchRecord[] => loadQueue();

/** Matches this browser has uploaded, so the player can see that sharing works. */
interface SentLog {
  readonly ids: readonly string[];
  readonly last: string | null;
}

const loadSent = (): SentLog =>
  readJson(SENT_KEY, (v) => {
    const x = v as Partial<SentLog> | null;
    return x && Array.isArray(x.ids)
      ? {
          ids: x.ids.filter((id): id is string => typeof id === 'string'),
          last: typeof x.last === 'string' ? x.last : null,
        }
      : null;
  }) ?? { ids: [], last: null };

function markSent(id: string): void {
  const sent = loadSent();
  const ids = sent.ids.includes(id) ? sent.ids : [...sent.ids, id].slice(-MAX_SENT_IDS);
  writeJson(SENT_KEY, { ids, last: new Date().toISOString() } satisfies SentLog);
}

export interface MatchLogStatus {
  /** Matches uploaded from this browser. */
  readonly shared: number;
  /** When the last upload was confirmed (ISO 8601), or null. */
  readonly lastSharedAt: string | null;
  /** Matches waiting to be uploaded. */
  readonly waiting: number;
}

export function matchLogStatus(): MatchLogStatus {
  const sent = loadSent();
  return { shared: sent.ids.length, lastSharedAt: sent.last, waiting: loadQueue().length };
}

/** Whether a copy of a match has more of it than another: more actions, or a later update. */
const fuller = (a: MatchRecord, b: MatchRecord): boolean =>
  a.summary.actions > b.summary.actions ||
  (a.summary.actions === b.summary.actions && a.updatedAt > b.updatedAt);

/**
 * Queues a copy of a match to be sent again, unless a fuller copy is queued already: uploads
 * overlap, and an older one failing after a newer one never takes its place.
 */
function enqueue(record: MatchRecord): void {
  const queue = loadQueue();
  const queued = queue.find((r) => r.id === record.id);
  if (queued && !fuller(record, queued)) return;
  const rest = queue.filter((r) => r.id !== record.id);
  writeJson(QUEUE_KEY, [...rest, record].slice(-MAX_QUEUED));
}

/** Drops the queued copy of a match once a copy at least as recent has been delivered. */
function dequeue(id: string, actions: number): void {
  const queue = loadQueue();
  const rest = queue.filter((r) => !(r.id === id && r.summary.actions <= actions));
  if (rest.length !== queue.length) writeJson(QUEUE_KEY, rest);
}

async function post(record: MatchRecord): Promise<'sent' | 'retry' | 'drop'> {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(record),
    });
    if (res.ok) return 'sent';
    // The server refused this record (invalid, too large, from another site): retrying cannot
    // help. Anything else, such as a deployment whose endpoint is not up yet, is tried again.
    return res.status === 400 || res.status === 403 || res.status === 413 ? 'drop' : 'retry';
  } catch {
    return 'retry';
  }
}

async function deliver(record: MatchRecord): Promise<void> {
  const outcome = await post(record);
  if (outcome === 'retry') {
    enqueue(record);
    return;
  }
  dequeue(record.id, record.summary.actions);
  if (outcome === 'sent') markSent(record.id);
}

/**
 * Sends the matches that could not be uploaded before, one at a time: once uploading is turned
 * off, nothing more is sent, not even what this flush has not reached yet.
 */
export async function flushMatchQueue(): Promise<void> {
  const run = generation;
  for (const record of loadQueue()) {
    if (!enabled || generation !== run) return;
    await deliver(record);
  }
}

/**
 * One match's logger: create it when the match opens, call afterAction after every action and
 * leave when the match is closed. It uploads nothing while logging is off.
 */
export class MatchLogger {
  /** Actions in the last upload; a resumed match counts as already sent up to where it resumes. */
  private sent: number;
  private disposed = false;

  constructor(private readonly src: MatchLogSource) {
    this.sent = src.replay().actions.length;
    if (typeof document !== 'undefined')
      document.addEventListener('visibilitychange', this.onVisibility);
  }

  /** After every applied action: uploads when a round or the match has just ended. */
  afterAction(events: readonly MatchEvent[]): void {
    if (events.some((e) => e.type === 'RoundEnded' || e.type === 'MatchEnded')) this.send();
  }

  /** The match is closed (it may be resumed later): uploads what was played since the last upload. */
  leave(): void {
    if (this.disposed) return;
    if (this.changed()) this.send();
    this.dispose();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private changed(): boolean {
    return this.src.replay().actions.length !== this.sent;
  }

  private send(): void {
    if (!enabled || this.disposed) return;
    const record = buildRecord(this.src);
    if (record.summary.actions === 0) return;
    this.sent = record.summary.actions;
    void deliver(record);
  }

  /**
   * The tab is being hidden (and maybe closed): queue the match, then send it at once with a
   * beacon, which survives the page closing. A beacon's delivery is never confirmed, so the match
   * stays queued and is sent again on the next visit; the server keeps one copy per match.
   */
  private onVisibility = (): void => {
    if (!enabled || document.visibilityState !== 'hidden' || !this.changed()) return;
    const record = buildRecord(this.src);
    if (record.summary.actions === 0) return;
    this.sent = record.summary.actions;
    enqueue(record);
    const body = new Blob([JSON.stringify(record)], { type: 'application/json' });
    navigator.sendBeacon?.(ENDPOINT, body);
  };
}
